/**
 * Gestionnaire centralisé des erreurs API
 * Normalise et traite les erreurs provenant du backend
 */

/**
 * Parse les erreurs API (format DRF Django)
 * @param {Object} error - Erreur Axios
 * @returns {Object} { message: string, fieldErrors: Object, status: number, type: string }
 */
export const parseApiError = (error) => {
  if (!error) {
    return {
      message: 'Une erreur inattendue est survenue',
      fieldErrors: {},
      status: null,
      type: 'UNKNOWN',
    };
  }

  if (error.response) {
    const { status, data } = error.response;
    const fieldErrors = {};
    let message = 'Une erreur est survenue';

    // Parse les erreurs de validation (format DRF)
    if (typeof data === 'object' && data !== null) {
      // Erreur générale
      if (data.detail) {
        message = data.detail;
      } else if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
        message = data.non_field_errors[0] || message;
      } else {
        // Erreurs de champs
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'detail' && key !== 'non_field_errors') {
            if (Array.isArray(value)) {
              fieldErrors[key] = value[0] || '';
            } else if (typeof value === 'string') {
              fieldErrors[key] = value;
            }
          }
        });
      }
    }

    return {
      message,
      fieldErrors,
      status,
      type: getErrorType(status),
    };
  }

  if (error.request) {
    return {
      message: 'Le serveur ne répond pas. Vérifiez votre connexion réseau.',
      fieldErrors: {},
      status: null,
      type: 'NETWORK',
    };
  }

  return {
    message: error.message || 'Une erreur inattendue est survenue',
    fieldErrors: {},
    status: null,
    type: 'UNKNOWN',
  };
};

/**
 * Obtient le type d'erreur selon le statut HTTP
 * @param {number} status - Code de statut HTTP
 * @returns {string} Type d'erreur
 */
export const getErrorType = (status) => {
  switch (status) {
    case 400:
      return 'VALIDATION';
    case 401:
      return 'AUTH';
    case 403:
      return 'PERMISSION';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'UNPROCESSABLE';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'SERVER';
    default:
      return 'HTTP_ERROR';
  }
};


/**
 * Classe personnalisée pour les erreurs API
 */
export class ApiError extends Error {
  constructor(message, status, fieldErrors = {}, type = 'HTTP_ERROR') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.type = type;
  }

  /**
   * Crée une instance ApiError à partir d'une erreur Axios
   * @param {Object} error - Erreur Axios
   * @returns {ApiError}
   */
  static fromAxiosError(error) {
    const parsed = parseApiError(error);
    return new ApiError(
      parsed.message,
      parsed.status,
      parsed.fieldErrors,
      parsed.type
    );
  }
}
